import classNames from "classnames";
import {useEffect, useMemo} from "react";
import {useFormContext} from "react-hook-form";

import Button from "@/components/button/Button.tsx";
import {FileInput} from "@/components/fileInput/FileInput.tsx";
import {FormLabel} from "@/components/form/FormLabel.tsx";
import {FormSubtext} from "@/components/formSubtext/FormSubtext.tsx";
import {getFormError} from "@/lib/form/form.utils.ts";
import {URL_ORIGINAL_WEBSITE} from "@/lib/links.ts";
import {FieldProps} from "@/pages/checks/components/form/fields/common.ts";

import {ImagePreviewLabel} from "./ImagePreviewLabel.tsx";

import styles from "./ImageInputField.module.scss";

interface ImageInputFieldProps extends FieldProps {
  fallbackPreviewUrl?: string;
  fileTypes?: string;
  buttonLabel?: string;
  variant?: "default" | "report";
}

// Note: this component currently expects that the value to be a string if there is an existing image and that the string is a preview url,
// null if there is no file or the file has been removed, and a FileList if a new file has been selected.
export const ImageInputField = ({
  id,
  name,
  label,
  tooltip,
  required,
  labelIcon,
  disabled,
  fileTypes = "image/png, image/jpeg, image/webp",
  buttonLabel = "Choose File",
  placeholder,
  helpText,
  supportLink,
  supportLinkText,
  fallbackPreviewUrl,
  variant = "default",
}: ImageInputFieldProps) => {
  const {
    register,
    watch,
    resetField,
    setValue,
    formState: {errors},
  } = useFormContext();

  const value = watch(name);
  const hasValue = value && value.length > 0;
  const existingValue = value && typeof value === "string" && value !== "";
  const fieldError = getFormError(name, errors);

  const previewImageUrl = useMemo(() => {
    if (hasValue && !existingValue) {
      return URL.createObjectURL(value[0]);
    }
    if (existingValue) {
      return /^(https?:)?\/\//.test(value) ? value : `${URL_ORIGINAL_WEBSITE}${value}`;
    }
    if (fallbackPreviewUrl) {
      return fallbackPreviewUrl;
    }
    return undefined;
  }, [hasValue, value, existingValue, fallbackPreviewUrl]);

  const fileName = useMemo(() => {
    if (hasValue && !existingValue && value[0]) {
      return value[0].name;
    }
    if (existingValue) {
      return value.split("/").pop();
    }
    return placeholder;
  }, [hasValue, existingValue, value, placeholder]);

  // Opening the file dialog and then cancelling it leaves an empty FileList which we need to reset or the field will be marked as dirty
  useEffect(() => {
    if (value && value.length === 0) {
      if (existingValue) {
        setValue(name, null, {shouldDirty: true});
      } else {
        resetField(name);
      }
    }
  }, [existingValue, name, resetField, setValue, value]);

  return (
    <>
      <div className={classNames("d-flex gap-3 align-items-center", styles.imageInputField)}>
        {variant === "default" && (
          <ImagePreviewLabel variant={variant} id={id} name={name} previewImageUrl={previewImageUrl} />
        )}
        <div className={"w-100 align-self-start min-width-0"}>
          {label && (
            <FormLabel helpText={tooltip} forId={id || name} required={required} icon={labelIcon}>
              {label}
            </FormLabel>
          )}
          <FileInput
            id={name}
            fileName={fileName}
            disabled={disabled}
            accept={fileTypes}
            buttonLabel={buttonLabel}
            {...register(name)}
          />
          <FormSubtext
            className={"mt-1"}
            alwaysShowSubText={hasValue || existingValue}
            subText={
              hasValue || existingValue ? (
                <Button
                  variant={"linkBlue"}
                  typography={"normal"}
                  size={"fitContent"}
                  onClick={() => {
                    if (existingValue) {
                      setValue(name, null, {shouldDirty: true});
                    } else {
                      resetField(name);
                    }
                  }}
                >
                  Remove
                </Button>
              ) : (
                helpText
              )
            }
            supportLink={supportLink}
            supportLinkText={supportLinkText}
            error={fieldError?.toString()}
          />
        </div>
      </div>
      {variant === "report" && (
        <div className="mt-3">
          <ImagePreviewLabel variant={variant} id={id} name={name} previewImageUrl={previewImageUrl} />
        </div>
      )}
    </>
  );
};
